import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { PortalLayout } from '../components/layout/PortalLayout'
import { AuthLayout } from '../components/layout/AuthLayout'
import { ProtectedRoute } from '../components/auth/ProtectedRoute'
import { SuperuserProtectedRoute } from '../components/auth/SuperuserProtectedRoute'
import { CircularProgress, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'

// Lazy load page components for code splitting
const LoginPage = lazy(() => import('../pages/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const SuperuserLoginPage = lazy(() => import('../pages/auth/SuperuserLoginPage').then(m => ({ default: m.SuperuserLoginPage })))
const AccessDeniedPage = lazy(() => import('../pages/auth/AccessDeniedPage'))
const SessionExpiredPage = lazy(() => import('../pages/auth/SessionExpiredPage'))
const TenantSelectionPage = lazy(() => import('../pages/auth/TenantSelectionPage').then(m => ({ default: m.TenantSelectionPage })))
const DashboardPage = lazy(() => import('../pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
const EquipmentPage = lazy(() => import('../pages/equipment/EquipmentPage').then(m => ({ default: m.EquipmentPage })))
const InspectionsPage = lazy(() => import('../pages/inspections/InspectionsPage').then(m => ({ default: m.InspectionsPage })))
const InspectionChecklistPage = lazy(() => import('../pages/inspections/InspectionChecklistPage').then(m => ({ default: m.InspectionChecklistPage })))
const ChecklistPage = lazy(() => import('../pages/checklists/ChecklistPage').then(m => ({ default: m.ChecklistPage })))
const ChecklistAnalyticsPage = lazy(() => import('../pages/checklists/ChecklistAnalyticsPage').then(m => ({ default: m.ChecklistAnalyticsPage })))
const ChecklistResponsesPage = lazy(() => import('../pages/checklists/ChecklistResponsesPage').then(m => ({ default: m.ChecklistResponsesPage })))
const MaintenancePage = lazy(() => import('../pages/maintenance/MaintenancePage').then(m => ({ default: m.MaintenancePage })))
const MediaLibraryPage = lazy(() => import('../pages/media/MediaLibraryPage').then(m => ({ default: m.MediaLibraryPage })))
const InventoryPage = lazy(() => import('../pages/inventory/InventoryPage').then(m => ({ default: m.InventoryPage })))
const ReportsPage = lazy(() => import('../pages/reports/ReportsPage').then(m => ({ default: m.ReportsPage })))
const UsersPage = lazy(() => import('../pages/users/UsersPage').then(m => ({ default: m.UsersPage })))
const TenantsPage = lazy(() => import('../pages/tenants/TenantsPage').then(m => ({ default: m.TenantsPage })))
const DepartmentsPage = lazy(() => import('../pages/departments/DepartmentsPage').then(m => ({ default: m.DepartmentsPage })))
const SettingsPage = lazy(() => import('../pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))
const SuperuserDashboard = lazy(() => import('../pages/platform/SuperuserDashboard').then(m => ({ default: m.SuperuserDashboard })))
const PlatformOverview = lazy(() => import('../pages/platform/PlatformOverview').then(m => ({ default: m.PlatformOverview })))
const SuperuserSecuritySettings = lazy(() => import('../components/auth/SuperuserSecuritySettings').then(m => ({ default: m.SuperuserSecuritySettings })))
const TenantManagementPage = lazy(() => import('../pages/platform/TenantManagementPage').then(m => ({ default: m.TenantManagementPage })))
const CrossTenantUserManagementPage = lazy(() => import('../pages/platform/CrossTenantUserManagementPage').then(m => ({ default: m.CrossTenantUserManagementPage })))
const RolePermissionManagementPage = lazy(() => import('../pages/platform/RolePermissionManagementPage').then(m => ({ default: m.RolePermissionManagementPage })))
const AuditMonitoringPage = lazy(() => import('../pages/platform/AuditMonitoringPage').then(m => ({ default: m.AuditMonitoringPage })))
const SecuritySettingsPage = lazy(() => import('../pages/platform/SecuritySettingsPage').then(m => ({ default: m.SecuritySettingsPage })))
const PermissionsManagementPage = lazy(() => import('../pages/users/PermissionsManagementPage').then(m => ({ default: m.PermissionsManagementPage })))
const PermissionRequestsAdminPage = lazy(() => import('../pages/users/PermissionRequestsAdminPage').then(m => ({ default: m.PermissionRequestsAdminPage })))
const PlatformPermissionRequestsPage = lazy(() => import('../pages/platform/PlatformPermissionRequestsPage').then(m => ({ default: m.PlatformPermissionRequestsPage })))
const PermissionExamplesPage = lazy(() => import('../pages/examples/PermissionExamplesPage').then(m => ({ default: m.PermissionExamplesPage })))
const ShadcnDemo = lazy(() => import('../pages/ShadcnDemo'))
const AboutPage = lazy(() => import('../pages/AboutPage').then(m => ({ default: m.AboutPage })))
const NotFoundPage = lazy(() => import('../pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const EngineerPortalPage = lazy(() => import('../pages/portal/EngineerPortalPage').then(m => ({ default: m.EngineerPortalPage })))
const TaskAttachmentPage = lazy(() => import('../pages/portal/TaskAttachmentPage').then(m => ({ default: m.TaskAttachmentPage })))

// Loading fallback component
const PageLoadingFallback = () => {
  const { t } = useTranslation()
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        width: '100%',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{t('common.loading')}</Box>
    </Box>
  )
}

const PlatformSettingsPlaceholder = () => {
  const { t } = useTranslation()
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 3, textAlign: 'center' }}>
      <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>⚙️</Box>
      <Box>
        <Box sx={{ typography: 'h5', fontWeight: 600, color: 'text.primary', mb: 1 }}>{t('platform.platformSettings')}</Box>
        <Box sx={{ typography: 'body1', color: 'text.secondary' }}>{t('platform.underDevelopment')}</Box>
      </Box>
    </Box>
  )
}

// Wrapper component to add Suspense boundary
const LazyPage = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>
)

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LazyPage><LoginPage /></LazyPage>,
  },
  {
    path: '/session-expired',
    element: <LazyPage><SessionExpiredPage /></LazyPage>,
  },
  {
    path: '/access-denied',
    element: <LazyPage><AccessDeniedPage /></LazyPage>,
  },
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'login',
        element: <LazyPage><LoginPage /></LazyPage>,
      },
      {
        path: 'select-tenant',
        element: <LazyPage><TenantSelectionPage /></LazyPage>,
      },
      {
        path: '',
        element: <Navigate to="/auth/login" replace />,
      },
    ],
  },
  {
    path: '/platform',
    children: [
      {
        path: 'auth/login',
        element: <LazyPage><SuperuserLoginPage /></LazyPage>,
      },
      {
        path: '',
        element: (
          <SuperuserProtectedRoute>
            <LazyPage><SuperuserDashboard /></LazyPage>
          </SuperuserProtectedRoute>
        ),
        children: [
          {
            path: '',
            element: <Navigate to="/platform/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: <LazyPage><PlatformOverview /></LazyPage>,
          },
          {
            path: 'settings/security',
            element: <LazyPage><SecuritySettingsPage /></LazyPage>,
          },
          {
            path: 'tenants',
            element: <LazyPage><TenantManagementPage /></LazyPage>,
          },
          {
            path: 'users',
            element: <LazyPage><CrossTenantUserManagementPage /></LazyPage>,
          },
          {
            path: 'roles',
            element: <LazyPage><RolePermissionManagementPage /></LazyPage>,
          },
          {
            path: 'audit',
            element: <LazyPage><AuditMonitoringPage /></LazyPage>,
          },
          {
            path: 'permission-requests',
            element: <LazyPage><PlatformPermissionRequestsPage /></LazyPage>,
          },
          {
            path: 'settings',
            element: (
              <LazyPage>
                <PlatformSettingsPlaceholder />
              </LazyPage>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/portal',
    element: (
      <ProtectedRoute>
        <PortalLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="/portal/tasks" replace />,
      },
      {
        path: 'tasks',
        element: <LazyPage><EngineerPortalPage /></LazyPage>,
      },
      {
        path: 'tasks/:type/:id',
        element: <LazyPage><TaskAttachmentPage /></LazyPage>,
      },
    ]
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <LazyPage><DashboardPage /></LazyPage>,
      },
      {
        path: 'equipment',
        element: <LazyPage><EquipmentPage /></LazyPage>,
      },
      {
        path: 'inspections',
        element: <LazyPage><InspectionsPage /></LazyPage>,
      },
      {
        path: 'inspections/checklists',
        element: <LazyPage><ChecklistPage /></LazyPage>,
      },
      {
        path: 'checklists/analytics',
        element: <LazyPage><ChecklistAnalyticsPage /></LazyPage>,
      },
      {
        path: 'checklists/responses',
        element: <LazyPage><ChecklistResponsesPage /></LazyPage>,
      },
      {
        path: 'inspections/parameters',
        element: <LazyPage><InspectionChecklistPage /></LazyPage>,
      },
      {
        path: 'maintenance',
        element: <LazyPage><MaintenancePage /></LazyPage>,
      },
      {
        path: 'media',
        element: <LazyPage><MediaLibraryPage /></LazyPage>,
      },
      {
        path: 'inventory',
        element: <LazyPage><InventoryPage /></LazyPage>,
      },
      {
        path: 'reports',
        element: <LazyPage><ReportsPage /></LazyPage>,
      },
      {
        path: 'users',
        element: <LazyPage><UsersPage /></LazyPage>,
      },
      {
        path: 'tenants',
        element: <LazyPage><TenantsPage /></LazyPage>,
      },
      {
        path: 'departments',
        element: <LazyPage><DepartmentsPage /></LazyPage>,
      },
      {
        path: 'settings',
        element: <LazyPage><SettingsPage /></LazyPage>,
      },
      {
        path: 'my-permissions',
        element: <LazyPage><PermissionsManagementPage /></LazyPage>,
      },
      {
        path: 'admin/permission-requests',
        element: <LazyPage><PermissionRequestsAdminPage /></LazyPage>,
      },
      {
        path: 'examples/permissions',
        element: <LazyPage><PermissionExamplesPage /></LazyPage>,
      },
      {
        path: 'examples/shadcn',
        element: <LazyPage><ShadcnDemo /></LazyPage>,
      },
      {
        path: 'about',
        element: <LazyPage><AboutPage /></LazyPage>,
      },
    ],
  },
  {
    path: '*',
    element: <LazyPage><NotFoundPage /></LazyPage>,
  },
])